import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/store/authStore";
import { companyService } from "@/services/companyService";

const DISMISS_KEY = "affiliate_referral_notice_dismissed";

/** One-time welcome modal for companies that signed up via an affiliate link. */
export const AffiliateReferralNotice = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [open, setOpen] = useState(false);
  const [dismissedLocally, setDismissedLocally] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1",
  );

  const companyQuery = useQuery({
    queryKey: ["me", "company"],
    queryFn: companyService.getMyCompany,
    enabled: isAuthenticated && !dismissedLocally,
    staleTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    if (dismissedLocally) return;
    if (companyQuery.data?.affiliate_referrer_user_id) {
      setOpen(true);
    }
  }, [companyQuery.data, dismissedLocally]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissedLocally(true);
    setOpen(false);
  };

  if (!isAuthenticated || dismissedLocally) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Welcome to your workspace
          </DialogTitle>
          <DialogDescription>
            Your company signed up through a referral link. You are all set to explore the
            platform with your team.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleDismiss}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AffiliateReferralNotice;
